import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { GlobalIcon, IconImage } from 'src/modules/global';
import { Domains } from 'src/store';
import { NewsletterPageTypes } from 'src/modules/pages/components/newsletter/PagesNewsletter';
import classnames from 'classnames';
import { Row, Col, Input, Form, Select, Checkbox, AutoComplete } from 'antd';
import { defaultHorizontalGutter } from 'src/utils/grid';
import { Button } from 'src/modules/global/components/button/Button';
import { useIntl } from 'react-intl';
import { OutputChannels } from 'src/modules/locale';
import { getCurrentUrlHostname } from 'src/utils/url';
import styles from './GlobalFormNewsletter.module.scss';
import countriesGerman from './countriesGerman';
import countriesEnglish from './countriesEnglish';
import countriesEnglishAndGerman from './countriesEnglishAndGerman';

enum WebsiteLanguageValues {
  deGerman = 'de_german',
  deEnglish = 'de_english',
  usEnglish = 'us_english',
}

export interface GlobalFormNewsletterProps {
  onFinish: (values: any) => Promise<void>;
  buttonLabel?: string;
  data?: any;
  type: NewsletterPageTypes;
  topMargin?: boolean;
}

const getEnglishCountryName = (locale: string, countryName: string) => {
  if (locale === 'en') return countryName;

  const country = _.find(countriesEnglishAndGerman, ({ german: germanCountryName }) => {
    return countryName === germanCountryName;
  });

  if (country?.english) return country.english;

  return countryName;
};

export const GlobalFormNewsletter: React.FunctionComponent<GlobalFormNewsletterProps> = ({
  onFinish,
  buttonLabel,
  data,
  type = NewsletterPageTypes.Register,
  topMargin = false,
}) => {
  const router = useRouter();
  const { locale = 'en', query = {} } = router;
  const { outputChannel = OutputChannels.DE } = query;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const [checkPolicy, setCheckPolicy] = useState(false);
  const [submitClick, setSubmitClick] = useState(true);
  const [business, setBusiness] = useState(false);
  const intl = useIntl();
  const { deEnglish, deGerman, usEnglish } = WebsiteLanguageValues;

  const [form] = Form.useForm();
  const { Option } = Select;

  const suffixIcon = (
    <GlobalIcon image={IconImage.DownArrow} size="custom" className={styles.downArrow} />
  );

  const isRegistration = type === NewsletterPageTypes.Register;
  useEffect(() => {
    const newData = { ...data };
    switch (data?.NL_Source_Website) {
      case Domains.deProd:
        newData.WebsiteLanguage = data.Language === 'German' ? deGerman : deEnglish;
        break;
      case Domains.usaProd:
        newData.WebsiteLanguage = usEnglish;
        break;
      default:
        newData.WebsiteLanguage = deEnglish;
        break;
    }
    if (newData.Interest === 'Business') {
      setBusiness(true);
    }
    form.setFieldsValue(newData);
  }, [form, data, deEnglish, deGerman, usEnglish]);

  const _onFinish = (values) => {
    let newValues = { ...values };
    switch (newValues.WebsiteLanguage) {
      case deGerman:
        newValues = { ...newValues, Language: 'German', NL_Source_Website: 'www.walterknoll.de' };
        break;
      case deEnglish:
        newValues = { ...newValues, Language: 'English', NL_Source_Website: 'www.walterknoll.de' };
        break;
      case usEnglish:
        newValues = { ...newValues, Language: 'English', NL_Source_Website: 'www.walter-k.com' };
        break;
      default:
        break;
    }

    if (isRegistration) {
      newValues.NL_Source_Website = getCurrentUrlHostname();
    }
    delete newValues.WebsiteLanguage;

    newValues.Country = getEnglishCountryName(locale, newValues.Country);

    onFinish(newValues);
  };

  let isFirstColumn = true;
  const toggle2ColumnProps = () => {
    const colProps = isFirstColumn
      ? { xs: { span: 24, offset: 0 }, md: { span: 6, offset: 6 } }
      : { xs: { span: 24, offset: 0 }, md: { span: 6 } };
    isFirstColumn = !isFirstColumn;
    return colProps;
  };

  return (
    <div className={classnames(styles.container, topMargin && styles.topMargin)}>
      <Form
        layout="vertical"
        size="large"
        form={form}
        onFinish={_onFinish}
        initialValues={{
          Salutation: null,
          FirstName: null,
          LastName: null,
          Email: null,
          country: null,
        }}>
        <Row gutter={[defaultHorizontalGutter, 0]}>
          <Col xs={{ span: 24, offset: 0 }} md={{ span: 6, offset: 6 }}>
            <Form.Item
              className={styles.formItem}
              label={intl.formatMessage({ id: 'form/salutation' })}
              name="Salutation"
              hasFeedback>
              <Select
                suffixIcon={suffixIcon}
                placeholder={intl.formatMessage({ id: 'form/salutation_placeholder' })}>
                <Option value="Mr.">{intl.formatMessage({ id: 'form/salutation/mr' })}</Option>
                <Option value="Ms.">{intl.formatMessage({ id: 'form/salutation/ms' })}</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={{ span: 24, offset: 0 }} md={{ span: 6, offset: 6 }} />

          <Col {...toggle2ColumnProps()}>
            <Form.Item
              className={styles.formItem}
              label={intl.formatMessage({ id: 'form/first_name' })}
              name="FirstName">
              <Input type="text" max={40} />
            </Form.Item>
          </Col>

          <Col {...toggle2ColumnProps()}>
            <Form.Item
              className={styles.formItem}
              label={intl.formatMessage({ id: 'form/last_name' })}
              name="LastName">
              <Input type="text" max={40} />
            </Form.Item>
          </Col>
          {isRegistration && (
            <Col {...toggle2ColumnProps()}>
              <Form.Item
                className={styles.formItem}
                label={intl.formatMessage({ id: 'form/email' })}
                name="Email"
                rules={[{ required: true, type: 'email' }]}>
                <Input max={80} type="text" />
              </Form.Item>
            </Col>
          )}
          <Col {...toggle2ColumnProps()}>
            <Form.Item
              className={styles.formItem}
              label={intl.formatMessage({ id: 'form/interest' })}
              name="Interest"
              rules={[{ required: true }]}>
              <Select
                id="Interest"
                suffixIcon={suffixIcon}
                onChange={(value) => setBusiness(value === 'Business' ? true : false)}
                placeholder={intl.formatMessage({ id: 'form/interest' })}>
                <Option value="Private">
                  {intl.formatMessage({ id: 'form/interest/private' })}
                </Option>
                <Option value="Business">
                  {intl.formatMessage({ id: 'form/interest/business' })}
                </Option>
                <Option value="Professional">
                  {intl.formatMessage({ id: 'form/interest/professional' })}
                </Option>
              </Select>
            </Form.Item>
          </Col>
          {isRegistration && (
            <Col {...toggle2ColumnProps()}>
              <Form.Item
                className={styles.formItem}
                label={intl.formatMessage({ id: 'form/language' })}
                name="Language"
                initialValue={locale === 'de' ? 'German' : 'English'}>
                <Select
                  id="Language"
                  suffixIcon={suffixIcon}
                  placeholder={intl.formatMessage({ id: 'form/language' })}>
                  <Option value="German">
                    {intl.formatMessage({ id: 'form/newsletter/german' })}
                  </Option>
                  <Option value="English">
                    {intl.formatMessage({ id: 'form/newsletter/english' })}
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          )}
          {!isRegistration && (
            <Col {...toggle2ColumnProps()}>
              <Form.Item
                className={styles.formItem}
                label={intl.formatMessage({ id: 'form/website_language' })}
                name="WebsiteLanguage"
                initialValue={
                  locale === 'de'
                    ? 'de_german'
                    : outputChannel === OutputChannels.DE
                    ? 'de_english'
                    : 'us_english'
                }>
                <Select
                  id="WebsiteLanguage"
                  suffixIcon={suffixIcon}
                  placeholder={intl.formatMessage({ id: 'form/language' })}>
                  <Option value={deGerman}>{intl.formatMessage({ id: 'form/de_german' })}</Option>
                  <Option value={deEnglish}>{intl.formatMessage({ id: 'form/de_english' })}</Option>
                  <Option value={usEnglish}>{intl.formatMessage({ id: 'form/us_english' })}</Option>
                </Select>
              </Form.Item>
            </Col>
          )}
          <Col {...toggle2ColumnProps()}>
            <Form.Item
              className={styles.formItem}
              label={intl.formatMessage({ id: 'form/country' })}
              name="Country"
              hasFeedback>
              {locale === 'de' ? (
                <AutoComplete
                  options={countriesGerman}
                  filterOption={(inputValue, option) =>
                    option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                  }
                />
              ) : (
                <AutoComplete
                  options={countriesEnglish}
                  filterOption={(inputValue, option) =>
                    option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                  }
                />
              )}
            </Form.Item>
          </Col>
          {business && (
            <>
              <Col {...toggle2ColumnProps()}>
                <Form.Item
                  className={styles.formItem}
                  label={intl.formatMessage({ id: 'form/business' })}
                  name="BusinessField">
                  <Select
                    id="Business"
                    suffixIcon={suffixIcon}
                    placeholder={intl.formatMessage({ id: 'form/business' })}>
                    <Option value="Retail">{intl.formatMessage({ id: 'form/retail' })}</Option>
                    <Option value="Interior Design">
                      {intl.formatMessage({ id: 'form/interior_design' })}
                    </Option>
                    <Option value="Architecture">
                      {intl.formatMessage({ id: 'form/architecture' })}
                    </Option>
                    <Option value="Business Customer">
                      {intl.formatMessage({ id: 'form/business_customer' })}
                    </Option>
                    <Option value="Press">{intl.formatMessage({ id: 'form/press' })}</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col {...toggle2ColumnProps()}>
                <Form.Item
                  className={styles.formItem}
                  label={intl.formatMessage({ id: 'form/company' })}
                  name="Company">
                  <Input type="text" max={255} />
                </Form.Item>
              </Col>
            </>
          )}
          {isRegistration && (
            <Col
              className={styles.checkboxCol}
              xs={{ span: 24, offset: 0 }}
              md={{ span: 12, offset: 6 }}>
              <Form.Item
                className={styles.formItem}
                valuePropName="checked"
                rules={[
                  {
                    required: true,
                    type: 'boolean',
                    transform: (value) => value || undefined,
                  },
                ]}
                name="terms">
                <Checkbox
                  className={submitClick && !checkPolicy ? styles.errorCheckbox : ''}
                  checked={checkPolicy}
                  onChange={(e) => setCheckPolicy(e.target.checked)}>
                  <span
                    className={styles.policyText}
                    dangerouslySetInnerHTML={{
                      __html: intl.formatMessage({ id: 'form/newsletter/checkboxPolicy' }),
                    }}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          )}

          <Col xs={{ span: 24, offset: 0 }} md={{ span: 12, offset: 6 }}>
            <Button
              className={styles.submitButton}
              type="primary"
              htmlType="submit"
              widthType="fluid"
              onClick={() => {
                setSubmitClick(true);
              }}>
              {buttonLabel || intl.formatMessage({ id: 'form/buttonSend' })}
            </Button>

            <div className={styles.mandatoryFields}>
              {intl.formatMessage({ id: 'form/mandatoryFields' })}
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default GlobalFormNewsletter;
